import type { Meter } from "@/models/meter"
import { ResourceName, resources } from "@/models/resource"

import type { IntervalItem, IntervalMeterIdentity } from "./interval.types"

export type IntervalDisplayUnit = IntervalItem["unit"]

const METER_SOURCE_TYPE_TO_RESOURCE: Record<string, ResourceName> = {
  Electricity: ResourceName.Electricity,
  Gas: ResourceName.NaturalGas,
  Water: ResourceName.Water,
}

const DISPLAY_UNIT_BY_RESOURCE: Partial<
  Record<ResourceName, IntervalDisplayUnit>
> = {
  [ResourceName.Electricity]: "kWh",
  [ResourceName.NaturalGas]: "MMBtu",
  [ResourceName.Water]: "gallons",
}

export const INTERVAL_USAGE_LIST_FILTERS = {
  measurement_types: "usage",
} as const

export const INTERVAL_RESOURCE_DISCOVERY_LIMIT = 200

export const meterStackKey: (meterId: number | string) => string = String

// The canonical key for a drawn meter series. Anything that matches interval
// rows back to a series (chart bars, legend selection, export filtering) must
// build the key here so the formats cannot drift apart.
export const meterFlowKey = (
  meterId: number | string,
  flowDirection: IntervalItem["flow_direction"]
): string => `${meterStackKey(meterId)}:${flowDirection}`

const resourceForMeter = (meter: Meter): ResourceName | null => {
  const sourceTypeName = meter.meterSourceType?.name
  if (!sourceTypeName) {
    return null
  }

  return METER_SOURCE_TYPE_TO_RESOURCE[sourceTypeName] ?? null
}

export const metersForResource = (
  meters: Meter[] | undefined,
  resource: ResourceName
): Meter[] =>
  (meters ?? []).filter((meter) => resourceForMeter(meter) === resource)

const getResourceOrder = (resource: ResourceName): number =>
  resources.find((item) => item.name === resource)?.order ??
  Number.MAX_SAFE_INTEGER

export const defaultResource = (
  options: readonly ResourceName[]
): ResourceName | null => {
  if (options.length === 0) {
    return null
  }

  return [...options].sort(
    (left, right) => getResourceOrder(left) - getResourceOrder(right)
  )[0]
}

export const displayUnitForResource = (
  resource: ResourceName
): IntervalDisplayUnit | undefined => DISPLAY_UNIT_BY_RESOURCE[resource]

export const intervalDisplayUnit = (
  items: readonly IntervalItem[]
): IntervalDisplayUnit | undefined => {
  const units = items
    .map((item) => item.unit?.trim())
    .filter((unit): unit is string => Boolean(unit))

  if (units.length === 0) {
    return undefined
  }

  const canonicalUnit = units[0].toLowerCase()
  const hasMixedUnits = units.some(
    (unit) => unit.toLowerCase() !== canonicalUnit
  )
  if (hasMixedUnits) {
    return undefined
  }

  return units[0]
}

export const latestIntervalStartDatetime = (
  items: readonly IntervalItem[]
): string | null => {
  let latest: string | null = null
  let latestMillis = Number.NEGATIVE_INFINITY

  for (const item of items) {
    const millis = Date.parse(item.start_datetime)
    if (Number.isNaN(millis) || millis <= latestMillis) {
      continue
    }

    latestMillis = millis
    latest = item.start_datetime
  }

  return latest
}

export const toMeterIdsQueryParam = (meters: Meter[]): string =>
  meters
    .map((meter) => meter.id)
    .filter((id) => Boolean(id))
    .join(",")

export const INTERVAL_METER_SOURCE_I18N_KEYS = {
  grid: "Grid/Utility Meter",
  solarOnsiteConsumption: "Solar Meter- Onsite Consumption",
  solarSentBackToGrid: "Solar Meter-Sent Back to Grid",
} as const

export type IntervalMeterSourceI18nKey =
  (typeof INTERVAL_METER_SOURCE_I18N_KEYS)[keyof typeof INTERVAL_METER_SOURCE_I18N_KEYS]

const INTERVAL_METER_SOURCE_I18N_KEY_SET = new Set<string>(
  Object.values(INTERVAL_METER_SOURCE_I18N_KEYS)
)

export const isIntervalMeterSourceI18nKey = (
  value: string
): value is IntervalMeterSourceI18nKey =>
  INTERVAL_METER_SOURCE_I18N_KEY_SET.has(value)

export const toIntervalMeterIdentity = (
  meter: Meter
): IntervalMeterIdentity => ({
  id: String(meter.id),
  isElectricity: resourceForMeter(meter) === ResourceName.Electricity,
  isOnSiteGeneration: meter.isOnSiteGeneration,
  label: meter.meterCode || meter.name || String(meter.id),
  meterProvider: meter.meterProvider ?? "",
})

export const toIntervalMeterSource = (
  meter: IntervalMeterIdentity | undefined,
  flowDirection: IntervalItem["flow_direction"]
): string => {
  if (!meter) {
    return ""
  }
  if (!meter.isElectricity) {
    return meter.meterProvider
  }
  if (flowDirection === "export") {
    return INTERVAL_METER_SOURCE_I18N_KEYS.solarSentBackToGrid
  }
  if (meter.isOnSiteGeneration) {
    return INTERVAL_METER_SOURCE_I18N_KEYS.solarOnsiteConsumption
  }
  return INTERVAL_METER_SOURCE_I18N_KEYS.grid
}
